/**
 * TRACK A GAME, the phone's bottom sheet (DESIGN-CHANGELOG 2026-09-03;
 * `design/GT Mobile Mine.dc.html`, the `trkShow` block). AA phase 2.
 *
 * 86vh, the list scrolling inside. A 44px Search Box over STEAM (Marcus,
 * 2026-09-04 — the prototype searched the catalog; `trackModalVM` on the
 * merge), a state line under it, then one 56px row per match with `TRACK ▸`
 * (40px, a thumb target) or `✓ ON IT`. Stays open for a second pick — the ✕
 * closes.
 */

import { CoverArt } from '../components/CoverArt.tsx';
import { SearchBox } from '../components/SearchBox.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { useArt, useQueueArt } from '../components/useArt.ts';
import { useQd } from '../store/index.ts';
import { trackModalVM } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';

type TrackRow = ReturnType<typeof trackModalVM>['rows'][number];

function Row({ g }: { g: TrackRow }): JSX.Element {
  const soloAdd = useQd((s) => s.soloAdd);
  const art = useArt(g.id + 'w', g.hdrCands);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 11,
        padding: '8px 14px',
        borderBottom: '1px solid var(--gt-line-0)',
        minHeight: 56,
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          position: 'relative',
          width: 60,
          height: 28,
          flex: 'none',
          background: g.fall,
          overflow: 'hidden',
          border: '1px solid var(--gt-line)',
        }}
      >
        <CoverArt style={{ position: 'absolute', inset: 0 }} fs={7} ini={g.ini} hasCover={art.hasCover} cover={art.cover} />
      </div>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
        <span
          style={{
            fontFamily: 'var(--gt-font-sans)',
            fontWeight: 600,
            fontSize: 12.5,
            color: 'var(--gt-text)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {g.n}
        </span>
        <span
          style={{
            fontFamily: MONO,
            fontSize: 8,
            letterSpacing: '.06em',
            color: 'var(--gt-faint)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {g.meta}
        </span>
      </div>
      {g.has ? (
        <span style={{ fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: 'var(--acc)', flex: 'none' }}>✓ ON IT</span>
      ) : (
        <div
          onClick={() => void soloAdd(g.id)}
          style={{
            height: 40,
            display: 'flex',
            alignItems: 'center',
            padding: '0 11px',
            fontFamily: MONO,
            fontSize: 9,
            letterSpacing: '.07em',
            color: 'var(--acc)',
            border: '1px solid var(--acc)',
            background: 'var(--accSoft)',
            cursor: 'pointer',
            flex: 'none',
            boxSizing: 'border-box',
          }}
        >
          TRACK ▸
        </div>
      )}
    </div>
  );
}

export default function TrackSheet(): JSX.Element {
  const s = useQd();
  const v = trackModalVM(s);

  // The picker's thumbnails. Steam's half of the list already carries its art —
  // `trkSearch` merges the `assets` its browse page returns, the way
  // `slices/browse.ts` does — but the CATALOG half is the tail of `s.catalog`,
  // which no pass asks about (`revalidateVisible` covers only the head). Ids
  // resolve for those and drop for the Steam rows, which is the right split.
  useQueueArt(v.rows.map((r) => r.id));

  return (
    <Sheet
      onClose={() => s.closeTrack()}
      label="Track a game sheet"
      maxHeight="86vh"
      header={{ title: 'TRACK A GAME', titleClr: 'var(--gt-text)', titleFs: '9.5px', note: "YOUR BACKLOG, NOT THE PARTY'S" }}
    >
      <div style={{ padding: '12px 14px', borderBottom: '1px solid var(--gt-line)' }}>
        <SearchBox
          value={v.q}
          onChange={(e) => s.setTrkQ(e.target.value)}
          onClear={() => s.setTrkQ('')}
          placeholder="SEARCH STEAM…"
          w="100%"
          ifs={12}
          pad="13px 12px"
        />
        {v.hasNote ? (
          <div style={{ marginTop: 8, fontFamily: MONO, fontSize: 9, letterSpacing: '.07em', color: v.busy ? 'var(--gt-muted)' : 'var(--gt-faint)' }}>
            {v.note}
          </div>
        ) : null}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {v.rows.map((g) => (
          <Row key={g.key} g={g} />
        ))}
        {v.empty ? (
          <div style={{ padding: '22px 14px', fontFamily: MONO, fontSize: 10, letterSpacing: '.07em', color: 'var(--gt-muted)' }}>
            {v.emptyTxt}
          </div>
        ) : null}
      </div>
    </Sheet>
  );
}
